import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Plus, Store, Users, Cpu, FolderKanban, Mail, Phone, MapPin } from "lucide-react";
import Header from "../components/layout/Header";
import { StatCard, Badge, SearchInput, Button, Table } from "../components/ui/index";
import { revendeursAPI } from "../api/services";
import { formatDateShort } from "../utils/dateUtils";
import AddRevendeurModal from "../components/modals/AddRevendeurModal";
import EditRevendeurModal from "../components/modals/EditRevendeurModal";
import ActionMenu from "../components/ui/ActionMenu";
import ConfirmDeleteModal from "../components/modals/ConfirmDeleteModal";

export default function Revendeurs() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [revendeurs, setRevendeurs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const res = await revendeursAPI.getAll({ search });
      setRevendeurs(res.data.data);
    } catch (err) {
      console.error("Erreur chargement revendeurs:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [search]); // eslint-disable-line

  const handleAdd = async (newRevendeur) => {
    try {
      await revendeursAPI.create(newRevendeur);
      setShowModal(false);
      fetchData();
    } catch (err) {
      console.error("Erreur création:", err);
    }
  };

  const handleDelete = async () => {
    try {
      await revendeursAPI.remove(deleteItem._id);
      setDeleteItem(null);
      fetchData();
    } catch (err) {
      console.error("Erreur suppression:", err);
    }
  };

  const handleEdit = async (data) => {
    try {
      await revendeursAPI.update(editItem._id, data);
      setEditItem(null);
      fetchData();
    } catch (err) {
      console.error("Erreur modification:", err);
    }
  };

  const totalClients = revendeurs.reduce((s, r) => s + (r.clients || 0), 0);
  const totalAppareils = revendeurs.reduce((s, r) => s + (r.appareils || 0), 0);

  return (
    <>
      <Header title={t('resellers.title')} subtitle={`${revendeurs.length} ${t('resellers.activeResellers')}`} />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-6">
        <StatCard icon={Store} iconBg="bg-blue-100 text-blue-600" label={t('resellers.totalResellers')} value={revendeurs.length} />
        <StatCard icon={Users} iconBg="bg-green-100 text-green-600" label={t('resellers.totalClients')} value={totalClients} onClick={() => navigate('/clients')} />
        <StatCard icon={FolderKanban} iconBg="bg-orange-100 text-orange-600" label={t('resellers.totalProjects')} value={revendeurs.reduce((s, r) => s + (r.projets || 0), 0)} onClick={() => navigate('/projets')} />
        <StatCard icon={Cpu} iconBg="bg-purple-100 text-purple-600" label={t('resellers.totalDevices')} value={totalAppareils} onClick={() => navigate('/noeuds')} />
        <Button onClick={() => setShowModal(true)} className="h-full justify-center">
          <Plus size={18} /> {t('resellers.addReseller')}
        </Button>
      </div>

      <div className="mb-4">
        <SearchInput value={search} onChange={setSearch} />
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">{t('common.loading')}</div>
      ) : (
        <Table headers={[t('resellers.colReseller'), t('resellers.colManager'), t('resellers.colContact'), t('resellers.colClients'), t('resellers.colProjects'), t('resellers.colDevices'), t('resellers.colStatus'), t('resellers.colActions')]}>
          {revendeurs.map((r) => (
            <tr key={r._id} className="hover:bg-gray-50 transition-colors">
              <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Store size={18} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">{r.nom}</p>
                    <p className="text-xs text-slate-400">{t('resellers.since')} {formatDateShort(r.createdAt)}</p>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 text-sm text-slate-600">{r.responsable}</td>
              <td className="px-6 py-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-xs text-slate-600"><Mail size={12} className="text-slate-400" />{r.email}</div>
                  {r.telephones?.[0] && <div className="flex items-center gap-2 text-xs text-slate-600"><Phone size={12} className="text-slate-400" />{r.telephones[0]}</div>}
                  {r.adresse && <div className="flex items-center gap-2 text-xs text-slate-600"><MapPin size={12} className="text-slate-400" />{r.adresse}</div>}
                </div>
              </td>
              <td className="px-6 py-4 text-sm font-medium text-slate-700">{r.clients || 0}</td>
              <td className="px-6 py-4 text-sm font-medium text-slate-700">{r.projets || 0}</td>
              <td className="px-6 py-4">
                <p className="text-sm font-semibold text-slate-800">{r.appareils || 0}</p>
                <p className="text-xs text-green-600 font-medium">▲ {r.evolution || "+0%"}</p>
              </td>
              <td className="px-6 py-4"><Badge label={r.statut || "Actif"} /></td>
              <td className="px-4 py-3 text-right">
                <ActionMenu
                  rowId={r._id}
                  openMenu={openMenu}
                  setOpenMenu={setOpenMenu}
                  onEdit={() => setEditItem(r)}
                  onDelete={() => setDeleteItem(r)}
                />
              </td>
            </tr>
          ))}
        </Table>
      )}

      <AddRevendeurModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onAdd={handleAdd}
      />

      <EditRevendeurModal
        isOpen={!!editItem}
        onClose={() => setEditItem(null)}
        onEdit={handleEdit}
        item={editItem}
      />

      <ConfirmDeleteModal
        isOpen={!!deleteItem}
        onClose={() => setDeleteItem(null)}
        onConfirm={handleDelete}
        itemName={deleteItem?.nom}
      />
    </>
  );
}
